import {cropsFollowImages} from "../libs/cropFollowsImage";
import {VISIBILITY_CATALOG_FIELD} from "data/defaultSceneVisibility";

/**
 * Field categories for scene merging.
 * Object fields are shallow-merged per sub-key across layers.
 * Scalar and array fields use "last defined wins".
 */
const OBJECT_FIELDS = [
  "titles",
  "subtitles",
  "subtitles2",
  "subtitles3",
  "names",
  "names2",
  "names3",
  "names4",
  "names5",
  "names6",
  "dates",
  "times",
  "badges",
  "links",
  "links2",
  "links3",
  "features",
  "versions",
  "eaps",
  "positions",
  "positions2",
  "productNames",
  "gdnTags",
  "tags",
  "gdnColorVariants",
  "uploadImages",
  "customBgs",
  "customBgUploaded",
  "logos",
  "logoVariants",
  "textColorLocked",
  "buttonColors",
  "bgX",
  "bgY",
  "bgScale",
  "bgRotate",
  "bgFlip",
  "bgOpacities",
  "bgBrightnesses",
  // Baked per-banner backgrounds and what each was cut from (libs/bakedBg.ts).
  "bakedBgs",
  "bakedBgMeta",
  "layouts",
  "variants",
  "bases",
  "uploadX",
  "uploadY",
  "uploadScale",
  "uploadRotate",
  "uploadFlip",
  "logoX",
  "logoY",
  "logoScale",
  "allProductsBgOptions",
  // Pixelation per-template / per-square maps — shallow-merged per sub-key
  // like the other keyed maps above. Source of truth: PIXELATION_SCENE_FIELDS
  // in contexts/setting.js (COART-1465).
  "pixelationCells",
  "pixelationColors",
  "pixelationPalettes",
  "pixelationSizeOverrides",
  "pixelationCoverageOverrides",
  "pixelationDirectionOverrides",
  // Per-template brush masks (libs/pixelationMask.ts): a row's mask of a
  // template replaces the mask of a less specific row as a whole.
  "pixelationMasks",
  // Per-template corner masks of clicked tiles (libs/pixelation.ts).
  "pixelationShapes",
];

const SCALAR_FIELDS = [
  "bgOption",
  "layoutOption",
  "variantOption",
  "fontOption",
  "iconOption",
  "isFontToggle",
  "isButtonEnabled",
  "gdnAccentColor",
  "isUppercase",
  "logoHidden",
  "base",
  "bgBrightness",
  "bgBlurValue",
  "bgOpacity",
  "customBg",
  // The banner of the background selector preview (libs/bgPreviewTemplate).
  "bgPreviewTemplate",
  "fontVariation",
  "titleFontSize",
  "generativeGradients",
  "generativeSpeed",
  "generativeCount",
  "generativeStops",
  // Pixelation gate + scalar knobs — "last defined wins". `pixelationEnabled`
  // must be here so the on/off state survives a multi-layer default-scene
  // merge, not just the knobs (COART-1465).
  "pixelationEnabled",
  "pixelationSource",
  "pixelationSizeCoefficient",
  "pixelationOpacity",
  "pixelationCoverage",
  "pixelationGradual",
  "pixelationOpacityRamp",
  "pixelationBlend",
  "pixelationSeed",
  "pixelationClickAction",
  // The edge the mosaic grows from, and the tile shapes (libs/pixelation.ts).
  "pixelationDirection",
  "pixelationTileShape",
  "pixelationTileRoundness",
  "pixelationTileRadius",
  // The dots shape of the overlay (libs/pixelationDots.ts).
  "pixelationShape",
  "pixelationDotPattern",
  "pixelationDotSizeCoefficient",
  "pixelationDotScale",
  "pixelationDotTone",
  "pixelationDotInvert",
  "pixelationDotColor",
  "pixelationDotStructure",
  "pixelationDotBackdrop",
];

const ARRAY_FIELDS = [
  "visible",
  VISIBILITY_CATALOG_FIELD,
  "locked",
  "order",
  "gdnTagOrder",
  // The page's banners (libs/bannerComposition.ts): the most specific row
  // that names a list gives the whole list.
  "hiddenTemplates",
  "extraTemplates",
];

// A field that no list above names still belongs to the scene. The merge keeps
// it with "last defined wins" — it must not drop it. A dropped field loads only
// while one layer matches, and goes away when a second layer appears.
const LISTED_FIELDS = new Set([...OBJECT_FIELDS, ...SCALAR_FIELDS, ...ARRAY_FIELDS]);

// The server ships its own copy of these lists (src/server/api/v3/resolve-defaults.ts
// — the deployed image carries only src/server). Exported so the parity test can
// compare the lists themselves: comparing merge *output* on fixtures only catches
// drift in fields the fixtures happen to mention, which is how the server copy
// silently stayed on the pre-COART-1465 list and dropped `pixelationEnabled`
// from every v3-resolved default scene (#567).
export const MERGE_FIELDS = Object.freeze({
  OBJECT_FIELDS,
  SCALAR_FIELDS,
  ARRAY_FIELDS,
});

/**
 * Merges an array of scene objects from least-specific to most-specific.
 *
 * - Object fields: shallow merge per sub-key (Object.assign({}, ...values))
 * - Scalar fields: last defined value wins
 * - Array fields: last defined value wins (replaced wholesale)
 *
 * @param {Array<Object|null>} scenes - Scenes ordered least → most specific
 * @returns {Object} Merged scene
 */
export function mergeScenes(scenes) {
  const defined = scenes.filter(Boolean);
  if (defined.length === 0) return null;
  if (defined.length === 1) return defined[0];

  const result = {};

  for (const field of OBJECT_FIELDS) {
    const layers = defined.map((s) => s[field]).filter(Boolean);
    if (layers.length > 0) {
      result[field] = Object.assign({}, ...layers);
    }
  }

  for (const field of SCALAR_FIELDS) {
    for (const scene of defined) {
      if (scene[field] !== undefined) {
        result[field] = scene[field];
      }
    }
  }

  for (const field of ARRAY_FIELDS) {
    for (const scene of defined) {
      if (scene[field] !== undefined) {
        result[field] = scene[field];
      }
    }
  }

  for (const scene of defined) {
    for (const [field, value] of Object.entries(scene)) {
      if (!LISTED_FIELDS.has(field) && value !== undefined) {
        result[field] = value;
      }
    }
  }

  return cropsFollowImages(defined, result);
}
